import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

/* ─── Services Data ────────────────────────────────────────────── */
const services = [
    {
        id: 1,
        image: 'assets/img/icon/sve-icon1.png',
        title: 'Modern Laboratories',
        description:
            'Our campus features well-equipped pharmaceutical laboratories that provide hands-on practical training in pharmaceutics, pharmacology, and pharmaceutical chemistry to build strong technical skills.',
    },
    {
        id: 2,
        image: 'assets/img/icon/sve-icon2.png',
        title: 'Experienced Faculty',
        description:
            'Learn from qualified and experienced faculty members dedicated to academic excellence, student mentoring, and professional development in pharmaceutical sciences.',
    },
    {
        id: 3,
        image: 'assets/img/icon/sve-icon3.png',
        title: 'Industry-Focused Training',
        description:
            'We emphasize practical learning, research exposure, and industry-oriented training to prepare students for successful careers in the pharmaceutical and healthcare sectors.',
    },
];

/* ─── Variants ─────────────────────────────────────────────────── */
const gridContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 55, scale: 0.94 },
    visible: {
        opacity: 1, y: 0, scale: 1,
        transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
    },
};

const textStagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.13, delayChildren: 0.3 } },
};

const textChild = {
    hidden: { opacity: 0, y: 15 },
    visible: {
        opacity: 1, y: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};

/* ─── Single Service Card ──────────────────────────────────────── */
function ServiceCard({ service }) {
    const iconRef  = useRef(null);
    const cardRef  = useRef(null);
    const { ref: inViewRef, inView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: icon spring entrance */
    useEffect(() => {
        if (inView && iconRef.current) {
            gsap.fromTo(
                iconRef.current,
                { scale: 0.5, opacity: 0, rotation: -15 },
                { scale: 1, opacity: 1, rotation: 0, duration: 0.7, ease: 'back.out(2)', delay: 0.25 }
            );
        }
    }, [inView]);

    /* GSAP: hover lift */
    const handleEnter = () => {
        gsap.to(cardRef.current, { y: -10, duration: 0.3, ease: 'power2.out' });
        gsap.to(iconRef.current, { scale: 1.12, rotation: 5, duration: 0.35, ease: 'power2.out' });
    };
    const handleLeave = () => {
        gsap.to(cardRef.current, { y: 0,  duration: 0.3, ease: 'power2.out' });
        gsap.to(iconRef.current, { scale: 1, rotation: 0, duration: 0.35, ease: 'power2.out' });
    };

    const setRefs = (el) => {
        cardRef.current = el;
        inViewRef(el);
    };

    return (
        <motion.div
            className="col-lg-4 col-md-6 col-sm-12"
            variants={cardVariants}
        >
            <div
                ref={setRefs}
                className="services-box mb-30 text-center"
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
                style={{ cursor: 'default' }}
            >
                <div className="sr-contner">

                    {/* Icon — GSAP spring */}
                    <div className="icon">
                        <img
                            ref={iconRef}
                            src={service.image}
                            alt="icon"
                            style={{ opacity: 0 }}
                        />
                    </div>

                    {/* Text — staggered */}
                    <motion.div
                        className="text"
                        variants={textStagger}
                        initial="hidden"
                        animate={inView ? 'visible' : 'hidden'}
                    >
                        <motion.h3 variants={textChild}>{service.title}</motion.h3>
                        <motion.p variants={textChild}>{service.description}</motion.p>
                    </motion.div>

                </div>
            </div>
        </motion.div>
    );
}

/* ─── Main Component ───────────────────────────────────────────── */
function Service() {
    const { ref: gridRef, inView: gridInView } = useInView({ threshold: 0.2, triggerOnce: true });

    return (
        <section className="service-details-one pt-120 pb-90 p-relative fix">
            <div className="container">
                <motion.div
                    ref={gridRef}
                    className="row"
                    variants={gridContainerVariants}
                    initial="hidden"
                    animate={gridInView ? 'visible' : 'hidden'}
                >
                    {services.map((service) => (
                        <ServiceCard key={service.id} service={service} />
                    ))}
                </motion.div>
            </div>
        </section>
    );
}

export default Service;
